import * as React from "react";
import { Link, useSearchParams } from "react-router";
import { signInSchema } from "@taskiq/shared";
import { AuthLayout } from "@/components/AuthLayout";
import { OAuthButtons } from "@/components/auth/OAuthButtons";
import { TurnstileWidget } from "@/components/auth/TurnstileWidget";
import { Button } from "@/components/ui/Button";
import { Field, Input, PasswordInput, Form } from "@/components/ui/Field";
import { FormMessage } from "@/components/ui/Feedback";
import { TextLink } from "@/components/ui/TextLink";
import { useAuth } from "@/lib/auth-context";
import { describeAuthError, fieldErrorsFrom } from "@/lib/auth-errors";
import { PLACEHOLDER } from "@/lib/placeholders";
import { useTurnstile } from "@/lib/turnstile";

/**
 * The two query flags come from the password screen, which signs the user out
 * after they set a password and sends them here. Without a word about it, that
 * looks like the save failed - they are back on a sign-in form having just
 * successfully changed something.
 */
const ARRIVAL_NOTE: Record<string, string> = {
  welcome: "Your password is set. Sign in to finish setting up your account.",
  reset: "Your password has been changed. Sign in with the new one.",
};

export function Login() {
  const { signIn } = useAuth();
  const [params] = useSearchParams();

  const arrival = Object.keys(ARRIVAL_NOTE).find((key) => params.has(key));

  const [values, setValues] = React.useState({ email: "", password: "" });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const captcha = useTurnstile();

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);

    // The same zod schema the API validates against, so the form can never
    // submit something the server was always going to reject.
    const parsed = signInSchema.safeParse(values);
    if (!parsed.success) {
      setFieldErrors(fieldErrorsFrom(parsed.error));
      return;
    }
    setFieldErrors({});
    setSubmitting(true);

    try {
      await signIn(parsed.data.email, parsed.data.password, captcha.token);
    } catch (err) {
      setFormError(describeAuthError(err));
      // The token is spent whether or not it was the reason. Without this, a
      // typo'd password leaves the next attempt failing as `captcha_failed` on
      // a password that was right.
      captcha.reset();
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Your crews, locations and assessments."
      // The one deliberate exception to "no navigation on an auth screen". Most
      // arrivals here came from the marketing nav's Sign in, and the only way
      // back was the browser button or guessing that the logo is a link - which
      // is a dead end on a page somebody opened to *look* at the product.
      back={{ to: "/", label: "Back to site" }}
      footer={
        <>
          New to TaskIQ? <TextLink to="/signup">Create an account</TextLink>
        </>
      }
    >
      <Form onSubmit={onSubmit}>
        {arrival && !formError && <p className="auth-note">{ARRIVAL_NOTE[arrival]}</p>}

        <Field label="Work email" htmlFor="email" required error={fieldErrors.email}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="username email"
            autoFocus
            placeholder={PLACEHOLDER.email}
            inputSize="md"
            invalid={Boolean(fieldErrors.email)}
            value={values.email}
            onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          required
          error={fieldErrors.password}
          // Beside the label rather than under the input: it belongs to the
          // password field, and a row of its own is 30px on a screen whose whole
          // constraint is fitting a laptop viewport without scrolling.
          labelAction={
            <Link to="/forgot-password" className="auth-forgot__link">
              Forgot?
            </Link>
          }
        >
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            inputSize="md"
            invalid={Boolean(fieldErrors.password)}
            value={values.password}
            onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
          />
        </Field>

        {/* Inline, never a toast - a toast auto-dismisses and a failed sign-in
            has to stay on screen until it is dealt with. */}
        <FormMessage>{formError}</FormMessage>

        <TurnstileWidget
          onToken={captcha.onToken}
          onUnavailable={captcha.onUnavailable}
          resetKey={captcha.resetKey}
        />

        <Button type="submit" size="lg" block loading={submitting} className="auth-submit">
          Sign in
        </Button>
      </Form>

      <div className="auth-resend">
        <OAuthButtons />
      </div>
    </AuthLayout>
  );
}
